/**
 * Validation logic for the Create Strings form.
 *
 * Pure functions — no side effects, no React dependency. Each getter returns a
 * user-facing error string, or `undefined` when the value is valid. Keeping the
 * rules here (rather than inline in the modal) lets the field errors surface
 * live as the user types and on submit from a single source of truth, so the
 * two paths can never disagree.
 */

import type { TranslateFn } from 'src/i18n/shim/useTranslation';
import {
  CONTEXT_NOTE_CHAR_LIMIT,
  MAX_STRINGS_PER_GROUP,
  SOURCE_TEXT_CHAR_LIMIT,
  STRING_ID_FORMAT_ERROR_KEY,
  STRING_ID_MAX_LENGTH,
  STRING_ID_PATTERN,
} from '../constants';

/** The subset of a string entry that participates in validation. */
export interface StringEntryValues {
  stringId: string;
  sourceText: string;
  contextNote: string;
}

export interface StringEntryErrors {
  stringId?: string;
  sourceText?: string;
  contextNote?: string;
}

/** Membership test for String IDs: `String1` and ` strinG1 ` are the same ID. */
export function includesStringId(ids: string[], stringId: string): boolean {
  const target = stringId.trim().toLowerCase();
  return ids.some((id) => id.trim().toLowerCase() === target);
}

/**
 * Validates a single entry's String ID.
 *
 * Every value is trimmed first because that is what gets submitted, so a value
 * that passes here cannot be rejected (or silently truncated) later. The
 * else-if chain fixes error precedence: required → format → max length →
 * uniqueness, so the most fundamental problem is reported first.
 *
 * Uniqueness is checked against strings already in the group, other additions
 * staged in the draft, and the other entries in this same batch (identified by
 * position, so an entry never flags itself).
 */
export function getStringIdError(
  t: TranslateFn,
  index: number,
  entries: StringEntryValues[],
  existingStringIds: string[],
  draftAddedStringIds: string[],
): string | undefined {
  const stringId = entries[index].stringId.trim();

  if (!stringId) {
    return t('sgd-create-string-id-required');
  }
  if (!STRING_ID_PATTERN.test(stringId)) {
    return t(STRING_ID_FORMAT_ERROR_KEY);
  }
  if (stringId.length > STRING_ID_MAX_LENGTH) {
    return t('sgd-string-id-too-long', { max: STRING_ID_MAX_LENGTH });
  }

  const allExistingIds = [...existingStringIds, ...draftAddedStringIds];
  if (includesStringId(allExistingIds, stringId)) {
    return t('sgd-create-string-id-duplicate-existing');
  }

  const otherIds = entries
    .filter((_, i) => i !== index)
    .map((entry) => entry.stringId.trim());
  if (includesStringId(otherIds, stringId)) {
    return t('sgd-create-string-id-duplicate-batch');
  }

  return undefined;
}

export function getSourceTextError(
  t: TranslateFn,
  sourceText: string,
): string | undefined {
  const trimmed = sourceText.trim();
  if (!trimmed) {
    return t('sgd-create-source-text-required');
  }
  if (trimmed.length > SOURCE_TEXT_CHAR_LIMIT) {
    return t('sgd-source-text-too-long', { max: SOURCE_TEXT_CHAR_LIMIT });
  }
  return undefined;
}

export function getContextNoteError(
  t: TranslateFn,
  contextNote: string,
): string | undefined {
  const trimmed = contextNote.trim();
  if (!trimmed) {
    return t('sgd-create-context-note-required');
  }
  if (trimmed.length > CONTEXT_NOTE_CHAR_LIMIT) {
    return t('sgd-context-note-too-long', { max: CONTEXT_NOTE_CHAR_LIMIT });
  }
  return undefined;
}

/** Computes every field error for one entry. */
export function getEntryErrors(
  t: TranslateFn,
  index: number,
  entries: StringEntryValues[],
  existingStringIds: string[],
  draftAddedStringIds: string[],
): StringEntryErrors {
  return {
    stringId: getStringIdError(
      t,
      index,
      entries,
      existingStringIds,
      draftAddedStringIds,
    ),
    sourceText: getSourceTextError(t, entries[index].sourceText),
    contextNote: getContextNoteError(t, entries[index].contextNote),
  };
}

/** True when an entry has no field errors. */
export function isEntryValid(errors: StringEntryErrors): boolean {
  return !errors.stringId && !errors.sourceText && !errors.contextNote;
}

/**
 * Error shown when submitting these entries would push the group past
 * {@link MAX_STRINGS_PER_GROUP}, or `null` when it fits.
 *
 * `existingTotal` is the group's resulting baseline size in the current
 * changeset (`server strings + draft additions − draft deletions`), so strings
 * marked for deletion free up headroom. `remainingSlots` is how many more the
 * user may add.
 */
export function getLimitError(
  t: TranslateFn,
  entryCount: number,
  existingTotal: number,
  remainingSlots: number,
): string | null {
  if (existingTotal + entryCount <= MAX_STRINGS_PER_GROUP) {
    return null;
  }
  return t('sgd-group-string-limit', {
    max: MAX_STRINGS_PER_GROUP,
    existing: existingTotal,
    remaining: remainingSlots,
  });
}
